import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { fetchSports, sportIcon } from "@/lib/sportsCatalog";
import { COUNTRIES } from "@/lib/countries";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, ImagePlus, X, Check, ChevronsUpDown, Info } from "lucide-react";
import { translateFr as tfr } from "@/i18n";

const MAX_LOGO_SIZE = 5 * 1024 * 1024;
const LOGO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function Combobox({ value, onChange, items, placeholder, searchPlaceholder, renderIcon, testid, emptyText = tfr("Aucun résultat") }) {
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal min-h-[42px] bg-white/[0.015] border-border hover:bg-secondary/50"
          data-testid={testid}
        >
          <span className={cn("flex items-center gap-2 truncate", !selected && "text-muted-foreground")}>
            {selected && renderIcon?.(selected)}
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[240px]" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList className="max-h-56">
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {items.map((i) => (
                <CommandItem
                  key={i.value}
                  value={`${i.label} ${i.keywords || ""}`}
                  onSelect={() => { onChange(i.value); setOpen(false); }}
                  data-testid={`${testid}-option-${i.value}`}
                >
                  {renderIcon?.(i)}
                  <span className="truncate">{i.label}</span>
                  {i.value === value && <Check className="ml-auto h-3.5 w-3.5 text-athlynx" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function LogoUpload({ file, onFile }) {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pick = (f) => {
    if (!f) return;
    if (!LOGO_TYPES.includes(f.type)) { toast.error(tfr("Ce type de fichier n'est pas accepté. (PNG, JPG ou WEBP)")); return; }
    if (f.size > MAX_LOGO_SIZE) { toast.error(tfr("Le logo dépasse la taille maximale autorisée (5 Mo).")); return; }
    onFile(f);
  };

  return (
    <div className="min-w-0">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
        data-testid="club-logo-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
        aria-label={tfr("Importer le logo du club")}
        className={cn(
          "w-full min-h-[200px] sm:min-h-[220px] flex flex-col items-center justify-center gap-2.5 p-4 rounded-xl border border-dashed text-center transition-colors",
          dragOver ? "border-athlynx/70 bg-athlynx/[0.05]" : "border-border/80 bg-white/[0.012] hover:border-foreground/35 hover:bg-white/[0.025]"
        )}
        data-testid="club-logo-dropzone"
      >
        {preview && file ? (
          <>
            <img src={preview} alt={tfr("Aperçu du logo")} className="h-[112px] w-[112px] object-contain rounded-[14px] bg-white/[0.04]" data-testid="club-logo-preview" />
            <div className="text-xs text-muted-foreground truncate max-w-full">{file.name}</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground/80 underline underline-offset-2">Remplacer</span>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onFile(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onFile(null); } }}
                className="inline-flex items-center gap-1 text-xs text-danger hover:underline underline-offset-2"
                data-testid="club-logo-remove"
              >
                <X className="h-3 w-3" /> Retirer
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="h-11 w-11 rounded-xl bg-secondary/70 border border-border flex items-center justify-center">
              <ImagePlus className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-sm font-medium">{tfr("Logo du club")}</div>
            <div className="text-xs text-muted-foreground leading-relaxed px-2">
              {tfr("Glissez-déposez ou cliquez pour importer une image")}
              <br />PNG, JPG ou WEBP — 5 Mo maximum
              <br />Format conseillé : carré, 500 × 500 px
            </div>
          </>
        )}
      </button>
    </div>
  );
}

const EMPTY_FORM = {
  name: "", sport: "", competition: "", countryCode: "FR", city: "", venue: "",
  contact_name: "", contact_role: "", contact_email: "", contact_phone: "",
  website: "", social: "", notes: "",
};

export default function AddClubDialog({ open, onOpenChange, onCreated }) {
  const [sports, setSports] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchSports().then(setSports).catch(() => toast.error(tfr("Impossible de charger le catalogue des sports")));
  }, [open]);

  const sport = useMemo(() => (sports || []).find((s) => s.id === form.sport), [sports, form.sport]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const touch = (k) => setTouched((p) => ({ ...p, [k]: true }));

  const normalizedWebsite = useMemo(() => {
    const w = form.website.trim();
    if (!w) return "";
    if (/^https?:\/\//i.test(w)) return w;
    if (/^[a-z0-9.-]+\.[a-z]{2,}([/?].*)?$/i.test(w)) return `https://${w}`;
    return w;
  }, [form.website]);

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = tfr("Le nom du club est obligatoire.");
    if (!form.sport) e.sport = tfr("Sélectionnez un sport.");
    if (form.sport && !form.competition) e.competition = tfr("Sélectionnez une compétition compatible avec ce sport.");
    if (!form.countryCode) e.country = tfr("Sélectionnez un pays.");
    if (!form.city.trim()) e.city = tfr("La ville est obligatoire.");
    if (!form.contact_name.trim()) e.contact_name = tfr("Le contact principal est obligatoire.");
    if (!form.contact_email.trim()) e.contact_email = "L'email du contact est obligatoire.";
    else if (!EMAIL_RE.test(form.contact_email.trim())) e.contact_email = "L'adresse e-mail n'est pas valide.";
    if (form.website.trim() && !/^https?:\/\/[^\s]+\.[^\s]+/i.test(normalizedWebsite)) e.website = "L'URL du site web n'est pas valide.";
    return e;
  }, [form, normalizedWebsite]);

  const invalid = Object.keys(errors).length > 0;

  const selectSport = (id) => {
    setForm((p) => ({ ...p, sport: id, competition: "" }));
  };

  const buildSocialLinks = () => {
    const raw = form.social.trim();
    if (!raw) return [];
    let url = raw;
    let platform = "instagram";
    if (/^@?[a-z0-9._]+$/i.test(raw)) {
      url = `https://instagram.com/${raw.replace(/^@/, "")}`;
    } else {
      if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
      const host = url.toLowerCase();
      platform = ["instagram", "linkedin", "twitter", "x.com", "facebook", "tiktok", "youtube"]
        .find((p) => host.includes(p)) || "other";
      if (platform === "x.com") platform = "twitter";
    }
    return [{ platform, url }];
  };

  const reset = () => { setForm(EMPTY_FORM); setLogoFile(null); setTouched({}); };

  const submit = async (e, force = false) => {
    e?.preventDefault();
    if (invalid || saving) {
      setTouched({ name: true, sport: true, competition: true, country: true, city: true, contact_name: true, contact_email: true, website: true });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sport: form.sport,
        competition: form.competition,
        country: COUNTRIES.find((c) => c.code === form.countryCode)?.label || form.countryCode,
        country_code: form.countryCode,
        city: form.city.trim(),
        venue: form.venue.trim(),
        contact_name: form.contact_name.trim(),
        contact_role: form.contact_role.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        website: normalizedWebsite,
        social_links: buildSocialLinks(),
        notes: form.notes.slice(0, 2000),
        force,
      };
      const r = await api.post("/clubs", payload);
      const club = r.data;
      if (logoFile) {
        try {
          const fd = new FormData();
          fd.append("file", logoFile);
          await api.post(`/clubs/${club.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          toast.error(tfr("Club créé, mais l'envoi du logo a échoué."));
        }
      }
      toast.success(tfr("Club créé"));
      onCreated?.(club);
      onOpenChange(false);
      reset();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        const detail = err.response.data?.detail;
        const existing = detail?.existing_club;
        const ok = window.confirm(
          `${tfr("Un club similaire existe déjà :")} « ${existing?.name || form.name} »${existing?.city ? ` (${existing.city})` : ""}.\n${tfr("Créer quand même ?")}`
        );
        if (ok) { setSaving(false); return submit(null, true); }
      } else if (status !== 402) {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : tfr("Erreur lors de la création du club"));
      }
    } finally {
      setSaving(false);
    }
  };

  const err = (k) => touched[k] && errors[k] ? (
    <p className="mt-1.5 text-xs text-danger" role="alert">{errors[k]}</p>
  ) : null;

  const sportItems = (sports || []).map((s) => ({
    value: s.id, label: s.label, keywords: (s.aliases || []).join(" "), id: s.id,
  }));
  const countryItems = COUNTRIES.map((c) => ({ value: c.code, label: c.label, keywords: c.code }));
  const competitionItems = (sport?.competitions || []).map((c) => ({
    value: c.id,
    label: c.country && c.country !== "France" ? `${c.label} · ${c.country}` : c.label,
  }));

  return (
    <Dialog open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <DialogContent
        className="w-[min(920px,calc(100vw-48px))] max-w-none max-h-[min(900px,calc(100dvh-40px))] p-0 gap-0 flex flex-col overflow-hidden rounded-[18px] max-sm:w-screen max-sm:h-[100dvh] max-sm:max-h-[100dvh] max-sm:rounded-none"
        data-testid="add-club-dialog"
      >
        {/* Header */}
        <div className="shrink-0 px-6 pt-6 pb-5 border-b border-border">
          <DialogTitle className="text-2xl font-semibold tracking-tight">{tfr("Nouveau club")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1.5">
            {tfr("Créez une fiche club complète pour votre portefeuille.")}
          </DialogDescription>
        </div>

        <form onSubmit={submit} className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
            {/* Identity section */}
            <div className="rounded-[14px] border border-border bg-white/[0.012] p-4 grid grid-cols-1 sm:grid-cols-[220px_minmax(0,1fr)] gap-5">
              <LogoUpload file={logoFile} onFile={setLogoFile} />
              <div className="min-w-0 space-y-4">
                <div>
                  <Label htmlFor="club-name">{tfr("Nom du club")} <span className="text-athlynx" aria-hidden>*</span></Label>
                  <Input
                    id="club-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    onBlur={() => touch("name")}
                    placeholder="ex: FC Versailles"
                    aria-invalid={!!(touched.name && errors.name)}
                    data-testid="add-club-name"
                  />
                  {err("name")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Sport <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Combobox
                      value={form.sport}
                      onChange={(v) => { selectSport(v); touch("sport"); }}
                      items={sportItems}
                      placeholder={tfr("Sélectionner un sport")}
                      searchPlaceholder={tfr("Rechercher un sport…")}
                      renderIcon={(i) => {
                        const Icon = sportIcon(i.id || i.value);
                        return <Icon className="h-3.5 w-3.5 mr-2 shrink-0 text-muted-foreground" />;
                      }}
                      testid="club-sport-select"
                      emptyText={tfr("Aucun sport trouvé")}
                    />
                    {err("sport")}
                  </div>
                  <div>
                    <Label>{sport?.competition_label || "Ligue / Championnat"} <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Combobox
                      value={form.competition}
                      onChange={(v) => { set("competition", v); touch("competition"); }}
                      items={competitionItems}
                      placeholder={form.sport ? tfr("Sélectionner une ligue") : tfr("Choisissez d'abord un sport")}
                      searchPlaceholder={tfr("Rechercher…")}
                      testid="club-competition-select"
                      emptyText={form.sport ? tfr("Aucune compétition") : tfr("Sélectionnez un sport")}
                    />
                    {err("competition")}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Pays <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Combobox
                      value={form.countryCode}
                      onChange={(v) => { set("countryCode", v); touch("country"); }}
                      items={countryItems}
                      placeholder={tfr("Sélectionner un pays")}
                      searchPlaceholder={tfr("Rechercher un pays…")}
                      testid="club-country-select"
                    />
                    {err("country")}
                  </div>
                  <div>
                    <Label htmlFor="club-city">Ville <span className="text-athlynx" aria-hidden>*</span></Label>
                    <Input
                      id="club-city"
                      value={form.city}
                      onChange={(e) => set("city", e.target.value)}
                      onBlur={() => touch("city")}
                      placeholder="ex: Versailles"
                      data-testid="add-club-city"
                    />
                    {err("city")}
                  </div>
                </div>
              </div>
            </div>

            {/* Venue + Contact */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="club-venue">{sport?.venue_label || "Stade / Salle"} <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
                <Input
                  id="club-venue"
                  value={form.venue}
                  onChange={(e) => set("venue", e.target.value)}
                  placeholder={sport?.venue_placeholder || "ex: Stade Montbauron"}
                  data-testid="add-club-venue"
                />
              </div>
              <div>
                <Label htmlFor="club-contact">Contact principal <span className="text-athlynx" aria-hidden>*</span></Label>
                <Input
                  id="club-contact"
                  value={form.contact_name}
                  onChange={(e) => set("contact_name", e.target.value)}
                  onBlur={() => touch("contact_name")}
                  placeholder={tfr("ex: Marc Lefèvre")}
                  data-testid="add-club-contact-name"
                />
                {err("contact_name")}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="club-role">Fonction</Label>
                <Input
                  id="club-role"
                  value={form.contact_role}
                  onChange={(e) => set("contact_role", e.target.value)}
                  placeholder="ex: Directeur sportif"
                  list="club-role-suggestions"
                  data-testid="add-club-contact-role"
                />
                <datalist id="club-role-suggestions">
                  {[tfr("Président"), tfr("Directeur sportif"), tfr("Responsable recrutement"), tfr("Entraîneur"), "General Manager",
                    "Scout", tfr("Responsable administratif"), tfr("Responsable juridique"), tfr("Responsable médical"),
                    tfr("Responsable partenariats")].map((r) => <option key={r} value={r} />)}
                </datalist>
              </div>
              <div>
                <Label htmlFor="club-email">Email <span className="text-athlynx" aria-hidden>*</span></Label>
                <Input
                  id="club-email"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set("contact_email", e.target.value)}
                  onBlur={() => touch("contact_email")}
                  placeholder="contact@club.fr"
                  aria-invalid={!!(touched.contact_email && errors.contact_email)}
                  data-testid="add-club-contact-email"
                />
                {err("contact_email")}
              </div>
              <div>
                <Label htmlFor="club-phone">{tfr("Téléphone")}</Label>
                <Input
                  id="club-phone"
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => set("contact_phone", e.target.value)}
                  placeholder="ex: +33 6 12 34 56 78"
                  data-testid="add-club-contact-phone"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="club-website">Site web</Label>
                <Input
                  id="club-website"
                  value={form.website}
                  onChange={(e) => set("website", e.target.value)}
                  onBlur={() => touch("website")}
                  placeholder="ex: https://www.club.fr"
                  data-testid="add-club-website"
                />
                {err("website")}
              </div>
              <div>
                <Label htmlFor="club-social">{tfr("Réseaux / Instagram")} <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
                <Input
                  id="club-social"
                  value={form.social}
                  onChange={(e) => set("social", e.target.value)}
                  placeholder="ex: @fcversailles"
                  data-testid="add-club-social"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="club-notes">Notes <span className="text-muted-foreground text-xs">(optionnel)</span></Label>
              <Textarea
                id="club-notes"
                rows={3}
                maxLength={2000}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                placeholder={tfr("Informations complémentaires sur le club…")}
                data-testid="add-club-notes"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-t border-border bg-card">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 shrink-0" />
              {tfr("Les informations pourront être modifiées ensuite.")}
            </div>
            <div className="grid grid-cols-2 sm:flex items-center gap-2.5">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="add-club-cancel">
                {tfr("Annuler")}
              </Button>
              <Button
                type="submit"
                disabled={saving || invalid}
                title={invalid ? Object.values(errors)[0] : undefined}
                className="bg-athlynx text-white hover:bg-athlynx/90 min-w-[130px]"
                data-testid="add-club-submit"
              >
                {saving ? tfr("Création…") : tfr("Créer le club")}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
